import { generateCacheKey, shouldCacheRequest } from '@/lib/cacheKeyGenerator';

let store = null;

export function initializeCacheInterceptor(storeInstance, apiInstance) {
    store = storeInstance;

    apiInstance.interceptors.request.use(
        async (config) => {
            if (!store?.state?.apiCache?.config?.enabled) {
                return config;
            }

            if (config.skipCache || config.forceRefresh) {
                return config;
            }

            const method = config.method ?? 'GET';
            const url = config.url ?? '';
            const fullUrl = config.baseURL ? `${config.baseURL}${url}` : url;

            const cacheConfig = store.state.apiCache?.config;
            if (!shouldCacheRequest(method, fullUrl, config, cacheConfig)) {
                return config;
            }

            const params = config.params ?? null;
            let body = null;
            if (config.data) {
                try {
                    body = typeof config.data === 'string' ? JSON.parse(config.data) : config.data;
                } catch (e) {
                    body = config.data;
                }
            }

            const cacheKey = generateCacheKey(method, fullUrl, params, body);

            const cachedData = await store.dispatch('apiCache/getCachedResponse', cacheKey);

            if (cachedData !== null) {
                config.adapter = () => {
                    return Promise.resolve({
                        data: cachedData,
                        status: 200,
                        statusText: 'OK',
                        headers: {},
                        config: config,
                    });
                };
            }

            config._cacheKey = cacheKey;

            return config;
        },
        (error) => {
            return Promise.reject(error);
        },
    );

    apiInstance.interceptors.response.use(
        (response) => {
            if (!store?.state?.apiCache?.config?.enabled) {
                return response;
            }

            const config = response.config;

            if (config.skipCache || !config._cacheKey) {
                return response;
            }

            const method = config.method ?? 'GET';
            const url = config.url ?? '';
            const fullUrl = config.baseURL ? `${config.baseURL}${url}` : url;

            const cacheConfig = store.state.apiCache?.config;
            if (!shouldCacheRequest(method, fullUrl, config, cacheConfig)) {
                return response;
            }

            if (response.status >= 200 && response.status < 300) {
                store.dispatch('apiCache/setCachedResponse', {
                    key: config._cacheKey,
                    data: response.data,
                    headers: response.headers,
                });
            }

            const isOurApi =
                fullUrl.includes('/cpanel/') || fullUrl.includes(import.meta.env.VITE_API_BASE_URL);

            if (
                isOurApi &&
                (method === 'POST' || method === 'PUT' || method === 'DELETE' || method === 'PATCH')
            ) {
                const resourcePath = url.split('?')[0];

                if (method === 'POST' && resourcePath.includes('/cpanel/cars')) {
                    store.dispatch('apiCache/invalidateCache', 'GET:/cpanel/cars');
                } else if (method === 'PUT' || method === 'PATCH') {
                    if (resourcePath.includes('/cpanel/cars/')) {
                        const uuid = resourcePath.split('/').pop();
                        store.dispatch('apiCache/invalidateCache', `GET:/cpanel/cars/${uuid}`);
                        store.dispatch('apiCache/invalidateCache', 'GET:/cpanel/cars');
                    } else if (resourcePath.includes('/cpanel/drivers/')) {
                        store.dispatch('apiCache/invalidateCache', 'GET:/cpanel/drivers');
                        store.dispatch('apiCache/invalidateCache', 'GET:/cpanel/profile/simple');
                    } else if (resourcePath.includes('/cpanel/profile/')) {
                        store.dispatch('apiCache/invalidateCache', 'GET:/cpanel/profile/simple');
                    } else if (resourcePath.includes('/cpanel/trips/')) {
                        store.dispatch('apiCache/invalidateCachePattern', '^POST:/cpanel/shifts.*');
                    }
                } else if (method === 'DELETE') {
                    if (resourcePath.includes('/cpanel/cars/')) {
                        store.dispatch('apiCache/invalidateCache', 'GET:/cpanel/cars');
                    } else if (resourcePath.includes('/cpanel/drivers/')) {
                        store.dispatch('apiCache/invalidateCache', 'GET:/cpanel/drivers');
                        store.dispatch('apiCache/invalidateCache', 'GET:/cpanel/profile/simple');
                    }
                }
            }

            return response;
        },
        (error) => {
            return Promise.reject(error);
        },
    );
}
